import NestedFormController from '../../forms/controllers/nested_form_controller'
import { useEventListener } from 'stimulus-library'

/**
 * Custom stimulus controller for accepts_nested_attributes_for file uploads (e.g. within a creative variation).
 * Specialized behavior includes:
 * - Allow files to be treated as required; if so, and none are present, the form is put into an invalid state
 *   (through the FormController)
 * - Allow multiple files to be selected at once, and then create separate nested files (one for each)
 *
 * Count tracking is delegated to SignalDOMChildrenController (registered as `signal-count`) on the
 * file items container. This controller listens for the signal to update form validation state and
 * dispatch update events consumed by parent form controllers.
 */
export default class AttachedFilesController extends NestedFormController {
  static targets = ['canSubmitInput', 'file']
  static values = {
    count: Number,
    required: Boolean,
    formName: String,
    signalName: String,
  }

  connect() {
    super.connect()
    this.element.insertAdjacentHTML('beforeend', this.#canSubmitInputHTML)
    this.#updateCanSubmit()
    useEventListener(this, this.element, 'direct-upload:end', this.augmentDirectUploadFileInput)
  }

  augmentDirectUploadFileInput(event) {
    const { target, detail } = event
    const { file } = detail

    const hiddenField = document.createElement('input')
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("value", file.name);
    hiddenField.name = target.name.replace("[file]", "[filename]")
    target.parentElement.appendChild(hiddenField)
  }

  disconnect() {
    super.disconnect()
    this.canSubmitInputTarget.remove()
  }

  add(event) {
    super.add(event)
    this.countValue += 1
    this.#updateCanSubmit()
  }

  remove(event) {
    super.remove(event)
    this.countValue -= 1
    this.#updateCanSubmit()
  }

  #updateCanSubmit() {
    this.canSubmitInputTarget.value = this.countValue > 0 ? this.countValue : null
    this.canSubmitInputTarget.dispatchEvent(new Event('input', { bubbles: true }))
    this.dispatch('update', { detail: { fileCount: this.countValue } })
  }

  get #canSubmitInputHTML() {
    const classes = this.requiredValue ? 'required' : ''
    return `<input type="hidden" name="${this.formNameValue}[can_submit]" class="${classes}" data-attached-files-target="canSubmitInput" />`
  }

  /**
   * Monitor newly created file inputs for file selection, to handle multiple file selection situations
   */
  fileTargetConnected(fileInput) {
    const pendingFileSelection = this.#pendingFiles?.shift() || null
    if (this.setFilesAttribute(fileInput, pendingFileSelection)) {
      this.dispatchEvent(fileInput, 'input')
    }
    useEventListener(this, fileInput, 'change', this.distributeMultipleFileSelection)
  }

  /**
   * Handle multiple file selection by distributing the files one each to newly created file inputs
   */
  distributeMultipleFileSelection(event) {
    const fileInput = event.target
    this.#pendingFiles = Array.from(fileInput.files)
    this.setFilesAttribute(fileInput, this.#pendingFiles.shift())
    const additionalFilesNeeded = this.#pendingFiles.length
    for (let i = 0; i < additionalFilesNeeded; i++) {
      this.add()
    }
  }

  /**
   * Update the files attribute of a file input (hint not as easy as it sounds)
   * @returns {boolean} true if the files attribute was set, false otherwise
   */
  setFilesAttribute(fileInput, file) {
    if (!file) {
      return false
    }

    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    fileInput.files = dataTransfer.files
    return true
  }

  #pendingFiles
}
